import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AuditActorFacet, AuditEventFacets, Client } from '@/types';
import {
  STANDARD_STATUSES,
  actorFacetKey,
  actorFacetLabel,
  actorFacetName,
  actorKeyFallbackLabel,
  eventTypeLabel,
  formatUsd,
  statusLabel,
} from './auditMeta';

export interface FilterChip {
  key: string;
  label: string;
  /** Optional hover text (e.g. the raw event type). */
  title?: string;
  onRemove: () => void;
}

interface AuditFilterBarProps {
  facets?: AuditEventFacets;
  /** Clients to offer in the client filter (system scope only; empty = hidden). */
  clients?: Client[];
  clientId: string | null;
  eventTypes: string[];
  statuses: string[];
  actor: string | null;
  ip: string;
  onClientChange: (clientId: string | null) => void;
  onToggleEventType: (eventType: string) => void;
  onClearEventTypes: () => void;
  onToggleStatus: (status: string) => void;
  onClearStatuses: () => void;
  onActorChange: (actorKey: string | null) => void;
  onIpChange: (ip: string) => void;
  onClear: () => void;
  activeFilterCount: number;
  /** Removable chips for every active filter that a control doesn't already spell out. */
  chips?: FilterChip[];
}

/** Close a popover on outside mousedown or Escape (Escape is swallowed so the modal stays open). */
function useDismiss(open: boolean, ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, ref, onClose]);
}

const TRIGGER =
  'flex items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-400';
const POPOVER = 'absolute z-20 mt-1 max-h-72 overflow-y-auto rounded border border-gray-200 bg-white p-1 shadow-lg';

/** Server-side filters: client, event type (multi), status (multi), person (searchable), IP + chips. */
const AuditFilterBar: React.FC<AuditFilterBarProps> = ({
  facets,
  clients = [],
  clientId,
  eventTypes,
  statuses,
  actor,
  ip,
  onClientChange,
  onToggleEventType,
  onClearEventTypes,
  onToggleStatus,
  onClearStatuses,
  onActorChange,
  onIpChange,
  onClear,
  activeFilterCount,
  chips = [],
}) => {
  const clientKnown = !clientId || clients.some((c) => c.id === clientId);

  return (
    <div className="flex flex-wrap items-center gap-2 border-b bg-white px-4 py-1.5 text-xs">
      {/* Client filter (system scope) */}
      {clients.length > 0 && (
        <select
          value={clientId ?? ''}
          onChange={(e) => onClientChange(e.target.value || null)}
          className="max-w-[12rem] rounded border border-gray-300 bg-white px-2 py-1 focus:outline-none"
          title="Filter by client"
          aria-label="Filter by client"
        >
          <option value="">All clients</option>
          {!clientKnown && clientId && <option value={clientId}>Selected client</option>}
          {clients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      )}

      <EventTypeMenu facets={facets} selected={eventTypes} onToggle={onToggleEventType} onClear={onClearEventTypes} />
      <StatusMenu facets={facets} selected={statuses} onToggle={onToggleStatus} onClear={onClearStatuses} />
      <ActorPicker facets={facets} value={actor} onChange={onActorChange} />

      {/* IP filter */}
      <div className="relative">
        <input
          type="text"
          placeholder="IP address"
          aria-label="Filter by IP address"
          value={ip}
          onChange={(e) => onIpChange(e.target.value)}
          className="w-32 rounded border border-gray-300 bg-white py-1 pl-6 pr-2 font-mono placeholder-gray-400 focus:outline-none"
        />
        <i className="fas fa-network-wired absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400" aria-hidden="true" />
      </div>

      {/* Inline chips for the active filters. They wrap with the controls (flex-wrap row). */}
      {chips.map((chip) => (
        <span
          key={chip.key}
          title={chip.title}
          className="inline-flex max-w-[16rem] items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-gray-600 shadow-sm ring-1 ring-gray-200"
        >
          <span className="truncate">{chip.label}</span>
          <button
            type="button"
            onClick={chip.onRemove}
            className="text-gray-400 hover:text-red-500"
            title={`Remove filter: ${chip.label}`}
            aria-label={`Remove filter: ${chip.label}`}
          >
            <i className="fas fa-times-circle text-[11px]" aria-hidden="true" />
          </button>
        </span>
      ))}

      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={onClear}
          title="Clear all filters (keeps the date range)"
          className="flex items-center gap-1 rounded px-2 py-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
        >
          <i className="fas fa-xmark text-[10px]" aria-hidden="true" />
          Clear filters ({activeFilterCount})
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------------------
// Event type (multi-select, plain-language labels, raw type as tooltip)
// ---------------------------------------------------------------------------------------

const EventTypeMenu: React.FC<{
  facets?: AuditEventFacets;
  selected: string[];
  onToggle: (t: string) => void;
  onClear: () => void;
}> = ({ facets, selected, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = React.useCallback(() => setOpen(false), []);
  useDismiss(open, ref, close);

  // Every type the server knows about in this range, plus any selected type with no rows.
  const options = useMemo(() => {
    const list = [...(facets?.byEventType ?? [])];
    for (const t of selected) if (!list.some((o) => o.eventType === t)) list.push({ eventType: t, count: 0 });
    return list;
  }, [facets, selected]);

  const buttonText =
    selected.length === 0
      ? 'All event types'
      : selected.length === 1
        ? eventTypeLabel(selected[0])
        : `Event types (${selected.length})`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`${TRIGGER} ${selected.length ? 'border-blue-300 text-blue-700' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Filter by event type: ${buttonText}`}
        title={selected.length ? selected.join(', ') : 'Filter by event type'}
      >
        <i className="fas fa-list text-[10px] text-gray-400" aria-hidden="true" />
        <span className="max-w-[10rem] truncate">{buttonText}</span>
        <i className="fas fa-chevron-down text-[8px] text-gray-400" aria-hidden="true" />
      </button>
      {open && (
        <div className={`${POPOVER} w-72`} role="group" aria-label="Event types">
          {selected.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="mb-0.5 w-full rounded px-2 py-1 text-left text-[11px] text-blue-600 hover:bg-gray-50"
            >
              Show all event types
            </button>
          )}
          {options.length === 0 && <div className="px-2 py-1 text-[11px] text-gray-400">No event types</div>}
          {options.map((et) => (
            <label
              key={et.eventType}
              title={et.eventType}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50"
            >
              <input type="checkbox" checked={selected.includes(et.eventType)} onChange={() => onToggle(et.eventType)} />
              <span className="flex-1 truncate text-[11px] text-gray-700">{eventTypeLabel(et.eventType)}</span>
              <span className="text-[10px] tabular-nums text-gray-400">{et.count.toLocaleString()}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------------------
// Status (multi-select over the NEW status of deposit.status_changed rows)
// ---------------------------------------------------------------------------------------

const StatusMenu: React.FC<{
  facets?: AuditEventFacets;
  selected: string[];
  onToggle: (s: string) => void;
  onClear: () => void;
}> = ({ facets, selected, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const close = React.useCallback(() => setOpen(false), []);
  useDismiss(open, ref, close);

  // Standard statuses first (stable order), then any other status the server reports or is selected.
  const options = useMemo(() => {
    const byStatus = facets?.byStatus ?? [];
    const names: string[] = [...STANDARD_STATUSES];
    for (const s of byStatus) if (!names.includes(s.status)) names.push(s.status);
    for (const s of selected) if (!names.includes(s)) names.push(s);
    return names.map((status) => {
      const f = byStatus.find((b) => b.status === status);
      return { status, count: f?.count ?? 0, deposits: f?.deposits ?? 0, amount: f?.amount ?? 0 };
    });
  }, [facets, selected]);

  const buttonText =
    selected.length === 0 ? 'Any status' : selected.length === 1 ? `Status: ${statusLabel(selected[0])}` : `Statuses (${selected.length})`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`${TRIGGER} ${selected.length ? 'border-blue-300 text-blue-700' : ''}`}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={`Filter by new status: ${buttonText}`}
        title="Filter status changes by the status the deposit was moved TO"
      >
        <i className="fas fa-flag text-[10px] text-gray-400" aria-hidden="true" />
        <span className="max-w-[10rem] truncate">{buttonText}</span>
        <i className="fas fa-chevron-down text-[8px] text-gray-400" aria-hidden="true" />
      </button>
      {open && (
        <div className={`${POPOVER} w-64`} role="group" aria-label="New status">
          <div className="px-2 pb-1 pt-0.5 text-[10px] text-gray-400">Moved to status · changes</div>
          {selected.length > 0 && (
            <button
              type="button"
              onClick={onClear}
              className="mb-0.5 w-full rounded px-2 py-1 text-left text-[11px] text-blue-600 hover:bg-gray-50"
            >
              Any status
            </button>
          )}
          {options.map((o) => (
            <label
              key={o.status}
              title={`${o.count.toLocaleString()} changes · ${o.deposits.toLocaleString()} deposits · ${formatUsd(o.amount)}`}
              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50"
            >
              <input type="checkbox" checked={selected.includes(o.status)} onChange={() => onToggle(o.status)} />
              <span className="flex-1 truncate text-[11px] text-gray-700">{statusLabel(o.status)}</span>
              <span className="text-[10px] tabular-nums text-gray-400">{o.count.toLocaleString()}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------------------
// Person (searchable; value = actor key)
// ---------------------------------------------------------------------------------------

const ActorPicker: React.FC<{
  facets?: AuditEventFacets;
  value: string | null;
  onChange: (key: string | null) => void;
}> = ({ facets, value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const close = React.useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);
  useDismiss(open, ref, close);

  const entries = useMemo(
    () => (facets?.byActor ?? []).map((a) => ({ ...a, key: actorFacetKey(a) })) as AuditActorFacet[],
    [facets]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((a) =>
      [actorFacetName(a), a.actorEmail ?? '', a.company ?? ''].some((s) => s.toLowerCase().includes(q))
    );
  }, [entries, query]);

  const selectedEntry = value ? entries.find((a) => a.key === value) : undefined;
  const buttonText = !value ? 'All people' : selectedEntry ? actorFacetName(selectedEntry) : actorKeyFallbackLabel(value);

  const pick = (key: string | null) => {
    onChange(key);
    close();
  };

  // Row 0 = "All people"; rows 1..n = filtered entries.
  const rowCount = filtered.length + 1;
  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(rowCount - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex === 0) pick(null);
      else if (filtered[activeIndex - 1]) pick(filtered[activeIndex - 1].key);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          setOpen((s) => !s);
          setActiveIndex(0);
        }}
        className={`${TRIGGER} ${value ? 'border-blue-300 text-blue-700' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Filter by person: ${buttonText}`}
        title="Filter by person"
      >
        <i className="fas fa-user text-[10px] text-gray-400" aria-hidden="true" />
        <span className="max-w-[10rem] truncate">{buttonText}</span>
        <i className="fas fa-chevron-down text-[8px] text-gray-400" aria-hidden="true" />
      </button>
      {open && (
        <div className={`${POPOVER} w-72 p-0`}>
          <div className="sticky top-0 border-b bg-white p-1">
            <input
              autoFocus
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(e.target.value.trim() ? 1 : 0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Search people…"
              aria-label="Search people"
              className="w-full rounded border border-gray-300 px-2 py-1 text-[11px] focus:border-blue-400 focus:outline-none"
            />
          </div>
          <ul role="listbox" aria-label="People" className="p-1">
            <PersonOption
              label="All people"
              selected={!value}
              active={activeIndex === 0}
              onPick={() => pick(null)}
              onHover={() => setActiveIndex(0)}
            />
            {filtered.map((a, i) => (
              <PersonOption
                key={a.key}
                label={actorFacetLabel(a)}
                title={a.actorEmail ?? undefined}
                selected={value === a.key}
                active={activeIndex === i + 1}
                onPick={() => pick(a.key)}
                onHover={() => setActiveIndex(i + 1)}
              />
            ))}
            {filtered.length === 0 && (
              <li className="px-2 py-1 text-[11px] text-gray-400">{entries.length ? 'No matching people' : 'No people in this range'}</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

const PersonOption: React.FC<{
  label: string;
  title?: string;
  selected: boolean;
  active: boolean;
  onPick: () => void;
  onHover: () => void;
}> = ({ label, title, selected, active, onPick, onHover }) => (
  <li role="option" aria-selected={selected}>
    <button
      type="button"
      onClick={onPick}
      onMouseEnter={onHover}
      title={title ?? label}
      className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[11px] ${
        active ? 'bg-blue-50 text-blue-800' : 'text-gray-700 hover:bg-gray-50'
      }`}
    >
      <i className={`fas fa-check w-3 text-[9px] ${selected ? 'text-blue-600' : 'invisible'}`} aria-hidden="true" />
      <span className="truncate">{label}</span>
    </button>
  </li>
);

export default AuditFilterBar;
